import React, {FunctionComponent, useCallback, useLayoutEffect, useMemo, useState} from "react";
import {Button, Dialog, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Utils from "../../../../../../../core/services/utils/utils";
import Form, {FinalForm, MuiRFF, Yup} from "../../../../../../components/form";
import {Texts} from "../../../../../../../@bizkeytech/base/core";
import {useTranslation} from "react-i18next";

export interface PurchasePlanningInformationUpdateNameDialogProps {
    open: boolean;
    close: () => void;
    name: string;
    update: (name: string) => Promise<boolean>;
}

const PurchasePlanningInformationUpdateNameDialog: FunctionComponent<PurchasePlanningInformationUpdateNameDialogProps> = (props) => {
    const {update, close} = props;
    const formId = useMemo(() => Utils.createUUID(), []);
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const {t} = useTranslation("@bizkeytech", {keyPrefix: "base.core.formValidation"})

    const schema = useMemo(() => Yup.object().shape({
        name: Yup.string().required(Texts.FormValidation.requiredField(t)),
    }), [t]);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    type Schema = Yup.InferType<typeof schema>;

    const initialValues = useMemo<Partial<Schema>>(() => ({
        name: props.name as any,
    }), [props.name]);


    /**
     * Calls the update callback and awaits for its response.
     *
     * if successful, close the dialog, otherwise
     */
    const onSubmit = useCallback<FinalForm.Config<Schema>["onSubmit"]>(async (values) => {
        setSubmitting(true);
        const resultFlag = await update(values.name);
        setSubmitting(false);
        if (!isMounted()) {
            return;
        }
        if (resultFlag) {
            close();
        }
    }, [close, isMounted, update]);

    /**
     * Resets the import state when the dialog is opened.
     */
    useLayoutEffect(() => {
        if (props.open) {
            setSubmitting(false);
        }
    }, [props.open]);

    return (
        <>
            <Dialog
                open={props.open}
                onClose={close}
                maxWidth={'xs'}
                fullWidth
            >
                <DialogTitle fontWeight={600}>
                    Update Planning Session Name
                </DialogTitle>
                <DialogContent>
                    <DialogContentText marginBlockEnd={2}>
                        Please enter the new name for this planning.
                    </DialogContentText>
                    <Form<Schema, Partial<Schema>>
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        formProps={{id: formId}}
                    >
                        {({initialValues, values}) => {
                            return (
                                <Grid2 container spacing={2}>
                                    <Grid2 xs={12}>
                                        <MuiRFF.TextField
                                            name={'name'}
                                            label={"Name"}
                                            required={!!required.name}
                                            autoFocus
                                        />
                                    </Grid2>
                                    <Grid2 xs={12}>
                                        <Button
                                            size={'large'}
                                            type={'submit'}
                                            form={formId}
                                            fullWidth
                                            variant={'contained'}
                                            color={'primary'}
                                            disabled={submitting || initialValues.name === values.name || !values.name?.trim()?.length}
                                        >
                                            {
                                                submitting
                                                    ? "Updating..."
                                                    : "Update"
                                            }
                                        </Button>
                                    </Grid2>
                                </Grid2>
                            )
                        }}
                    </Form>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default PurchasePlanningInformationUpdateNameDialog;